import { useState } from 'react'
import {Copy, Check, GraduationCap, Users, ExternalLink} from 'lucide-react'
import toast from 'react-hot-toast'
import AbaInscritos from '@/components/AbaInscritos'
import IndicacaoCapacitacao from '@/components/IndicacaoCapacitacao'
import { copiarTexto } from '@/utils/clipboard'
import {
  CURSOS_AROMATERAPIA_LISTA,
  ORIENTACAO_CURSO_PRESENTE,
  linkCursoPublico,
} from '@/data/cursosAromaterapia'

interface AbaCursosAromaterapiaProps {
  isAdmin: boolean
  usuarioConsultora: string
  nomeConsultora: string
  filtrarPorConsultora: string | null
}

function CopiarBotao({ texto, label }: { texto: string; label: string }) {
  const [copiado, setCopiado] = useState(false)

  const copiar = async () => {
    try {
      await copiarTexto(texto)
      setCopiado(true)
      toast.success('Copiado!')
      setTimeout(() => setCopiado(false), 2000)
    } catch {
      toast.error('Erro ao copiar')
    }
  }

  return (
    <button
      type="button"
      onClick={copiar}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all min-h-[44px] ${
        copiado
          ? 'bg-[#D8F3DC] text-[#2D6A4F] border-2 border-[#2D6A4F]'
          : 'bg-[#2D6A4F] text-white hover:bg-opacity-90'
      }`}
    >
      {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {copiado ? 'Copiado!' : label}
    </button>
  )
}

export default function AbaCursosAromaterapia({
  isAdmin,
  usuarioConsultora,
  nomeConsultora,
  filtrarPorConsultora,
}: AbaCursosAromaterapiaProps) {
  const [visao, setVisao] = useState<'cursos' | 'inscritos'>('cursos')
  const slug = usuarioConsultora || 'monica'
  const linkCursoGota = `https://www.descubraseusoleos.com.br/curso?indicado_por=${slug}`
  const linkEquipe = `https://www.descubraseusoleos.com.br/capacitacao-equipe?indicado_por=${slug}`

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-cormorant font-semibold text-[#2C2C2A] mb-1 flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-[#2D6A4F]" />
          Cursos de Aromaterapia
        </h2>
        <p className="text-sm text-[#5F5E5A]">
          Links individuais para divulgação e acompanhamento de quem se inscreveu pelo seu link.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <button
          type="button"
          onClick={() => setVisao('cursos')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
            visao === 'cursos'
              ? 'bg-[#2D6A4F] text-white'
              : 'bg-white border border-[#EDE0D4] text-[#2C2C2A] hover:bg-[#F8F4EF]'
          }`}
        >
          <ExternalLink className="w-4 h-4" />
          Ver Cursos
        </button>
        <button
          type="button"
          onClick={() => setVisao('inscritos')}
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
            visao === 'inscritos'
              ? 'bg-[#2D6A4F] text-white'
              : 'bg-white border border-[#EDE0D4] text-[#2C2C2A] hover:bg-[#F8F4EF]'
          }`}
        >
          <Users className="w-4 h-4" />
          Ver Inscritos
        </button>
      </div>

      {visao === 'cursos' ? (
        <div className="space-y-12">
          <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-xl px-4 py-3 text-sm text-[#15803D] leading-relaxed">
            {ORIENTACAO_CURSO_PRESENTE}
          </div>
          <p className="text-sm text-[#5F5E5A]">
            Assinantes podem compartilhar livremente o link de qualquer um desses cursos, sem limite e sem créditos.
            A Jornada das 12 Emoções continua sendo o único produto pago por crédito (R$ 30/acesso).
          </p>

          <section className="space-y-4">
            <div>
              <h3 className="text-xl font-cormorant font-semibold text-[#2C2C2A]">Curso Introdutório</h3>
              <p className="text-sm text-[#5F5E5A] mt-1 leading-relaxed">
                Use estes links para apresentar a aromaterapia a clientes e novos membros.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                <div className="bg-[#52B788] px-6 py-4">
                  <h4 className="text-lg font-cormorant font-semibold text-white">
                    Uma Gota de Equilíbrio (cliente)
                  </h4>
                </div>
                <div className="p-6">
                  <p className="text-sm text-[#5F5E5A] mb-4">
                    Curso introdutório com certificado. Envie para quem quer aprender óleos essenciais para cuidar da saúde.
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#D8F3DC] border-2 border-[#2D6A4F] rounded-2xl px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#2D6A4F] font-medium uppercase tracking-wide mb-1">
                        Seu link rastreável
                      </div>
                      <div className="text-[#2D6A4F] font-medium text-sm break-all">{linkCursoGota}</div>
                    </div>
                    <CopiarBotao texto={linkCursoGota} label="Copiar link" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                <div className="bg-[#52B788] px-6 py-4">
                  <h4 className="text-lg font-cormorant font-semibold text-white">
                    Uma Gota de Equilíbrio (equipe)
                  </h4>
                </div>
                <div className="p-6">
                  <p className="text-sm text-[#5F5E5A] mb-4">
                    Envie para membros da sua equipe que já estão cadastrados e precisam se capacitar no curso introdutório.
                  </p>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#D8F3DC] border-2 border-[#2D6A4F] rounded-2xl px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#2D6A4F] font-medium uppercase tracking-wide mb-1">
                        Seu link rastreável
                      </div>
                      <div className="text-[#2D6A4F] font-medium text-sm break-all">{linkEquipe}</div>
                    </div>
                    <CopiarBotao texto={linkEquipe} label="Copiar link" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-4 pt-2 border-t border-[#EDE0D4]">
            <div className="pt-6">
              <h3 className="text-xl font-cormorant font-semibold text-[#2C2C2A]">Cursos de Aprofundamento</h3>
              <p className="text-sm text-[#5F5E5A] mt-1 leading-relaxed">
                Use estes cursos para aprofundar e fidelizar quem já conhece a aromaterapia.
              </p>
            </div>
            <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-xl px-4 py-4 text-sm text-[#78350F] leading-relaxed">
              💡 Estratégia de fidelização: incentive suas clientes a manterem um volume de compra mensal (ex: 100 PV) e ofereça um destes cursos como recompensa. Você define o volume que achar ideal para o seu público, já que quanto mais ela compra e mantém a rotina de uso, mais aprofundado o conhecimento que você oferece em troca. Isso fortalece o vínculo e aumenta a recorrência de compra.
            </div>
            {CURSOS_AROMATERAPIA_LISTA.map((curso) => {
              const link = linkCursoPublico(curso.id, slug)
              return (
                <div key={curso.id} className="bg-white rounded-2xl shadow-md overflow-hidden">
                  <div className="bg-[#2D6A4F] px-6 py-4">
                    <h4 className="text-lg font-cormorant font-semibold text-white">{curso.titulo}</h4>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-[#5F5E5A] mb-4">{curso.subtitulo}</p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-[#D8F3DC] border-2 border-[#2D6A4F] rounded-2xl px-5 py-4">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#2D6A4F] font-medium uppercase tracking-wide mb-1">
                          Seu link rastreável
                        </div>
                        <div className="text-[#2D6A4F] font-medium text-sm break-all">{link}</div>
                      </div>
                      <CopiarBotao texto={link} label="Copiar link" />
                    </div>
                  </div>
                </div>
              )
            })}
          </section>

          <section className="space-y-4 pt-2 border-t border-[#EDE0D4]">
            <div className="pt-6">
              <h3 className="text-xl font-cormorant font-semibold text-[#2C2C2A]">
                Link de Seleção para Negócio e Capacitação
              </h3>
              <p className="text-sm text-[#5F5E5A] mt-1 leading-relaxed">
                Envie este link para pessoas que preencheram o diagnóstico e indicaram interesse em gerar renda ou buscar um novo propósito. Esse link direciona para uma página de prospecção seguida do curso Uma Gota de Equilíbrio, ideal para apresentar a oportunidade de negócio a quem já demonstrou esse interesse.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="bg-[#2D6A4F] px-6 py-4">
                <h4 className="text-lg font-cormorant font-semibold text-white">
                  Link de Seleção para Negócio e Capacitação
                </h4>
              </div>
              <div className="p-6">
                <IndicacaoCapacitacao
                  usuarioConsultora={slug}
                  nomeConsultora={nomeConsultora || slug}
                />
              </div>
            </div>
          </section>
        </div>
      ) : (
        <AbaInscritos isAdmin={isAdmin} filtrarPorConsultora={filtrarPorConsultora} />
      )}
    </div>
  )
}
